import './proto-styles.css'
import { Pane } from 'tweakpane'
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ClearPass } from 'three/examples/jsm/postprocessing/ClearPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { SobelOperatorShader } from 'three/examples/jsm/shaders/SobelOperatorShader.js';
import { RGBShiftShader } from 'three/examples/jsm/shaders/RGBShiftShader.js';
// import { gsap } from "gsap";

// Helper Funcs
function remap(number, inMin, inMax, outMin, outMax) {
    return (number - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;
}

const randomInt = (min, max) => {
    return Math.floor(Math.random() * (max - min) + min);
}

const randomFloat = (min, max) => {
    return Math.random() * (max - min) + min;
}

// Objects
const PARAMS = {
    loaded: false,
    colorSaturation: 0.25,
    mouseReactive: true,
    showInitial: true,
    mode: 0,
    twodeerotate: false,
    twodeeColor1: '#ffffff',
    twodeeColor2: '#ffffff',
    image: 0,
    vapor: false,
    vaporNoiseIntensity: 0.8,
    vaporNoiseVignette: false,
    vaporNoiseAmount: 0.1,
    vaporNoiseType: 0,
    vaporNoiseSpeed: 0.01,
    vaporNoiseScale: 0.1,
    mouseSpeed: 0.2,
    zoom: 0.75,
    grungifyNoise: false,
    grungifyNoiseIntensity: 0.8,
    grungifyNoiseScale: 0.1,
    grungifyNoiseSpeed: 0.1,
    grungifyLineIntensity: 0.01,
    grungifyLines: 100,
    grungifyGlitch: false,
    gltichType: 1,
    glitchSep: 0.0005, //Math.random() / 30
    glitchAngle: 3.1, //randomFloat( - Math.PI, Math.PI )
    glitchSeed: 0.35, //Math.random() 
    glitchSeedX: 0.2, //randomFloat( - 1, 1 )
    glitchSeedY: 0.2, //randomFloat( - 1, 1 )
    glitchDistortionX: 0.5, //randomFloat( 0, 1 )
    glitchDistortionY: 0.5, //randomFloat( 0, 1 )
    glitchColumns: 0.01,
    hemiLight: true,
    hemiColor1: 0xB1E1FF,
    hemiColor2: 0xB97A20,
    hemiIntensity: 1,
    dirLight: true,
    dirColor: 0xFFFFFF,
    dirIntensity: 1,
    speed: 0.15,
    shape: 0,
    patternColor1: '#ffffff',
    patternColor2: '#ffffff',
    columns: 25,
    rows: 25,
    wide: 0.75,
    tall: 0.75,
    amp: 0.02,
    freq: 0.02,
    scale: 0.05,
    rainbow: false,
    regular: true,
    sticky: false,
    explosion: false,
    bigWarp: false,
    sobel: false,
    bloom: false,
    bloomStrength: 0.6,
    bloomRadius: 1,
    bloomThreshold: 0.6,
    rgbShift: false,
    rgbShiftAmount: 0.01,
    rgbShiftAngle: 0.2,
    bgColor: '#1C1C1C',
    showBgColor: false
};
const custom3DUniforms = {
    uTime: { value: 0 },
    uNoise: { value: PARAMS.grungifyNoise ? 1.0 : 0.0 },
    uNoiseIntensity: { value: PARAMS.grungifyNoiseIntensity },
    uNoiseScale: { value: remap(PARAMS.grungifyNoiseScale, 0, 1, 0.01, 20) },
    uNoiseSpeed: { value: remap(PARAMS.grungifyNoiseSpeed, 0, 1, 0, 10) },
    uLineIntensity: { value: PARAMS.grungifyLineIntensity },
    uLines: { value: PARAMS.grungifyLines },
};
const SIZES = {
    width: window.innerWidth,
    height: window.innerHeight
};
const MOUSE_POS = {
    currentX: 0,
    currentY: 0,
    targetX: 0,
    targetY: 0
};
const PIXEL_RATIO = Math.min(window.devicePixelRatio, 2);

const tpContainer = document.querySelector('.pane');
const pane = new Pane({
    title: 'Thrash Settings',
    container: tpContainer,
});
pane.addInput(PARAMS, 'mode', {
    options: {
        twodee: 0,
        threedee: 1,
        pattern: 2
    },
}).on('change', () => {
    twoFolder.hidden = PARAMS.mode != 0;
    threeFolder.hidden = PARAMS.mode != 1;
    gridFolder.hidden = PARAMS.mode != 2;
    updateExperiment();
    pane.refresh();
});
pane.addInput(PARAMS, 'showBgColor', { label: 'bg color' }).on('change', () => {
    bgColorPane.hidden = !PARAMS.showBgColor;
    renderer.setClearAlpha((PARAMS.showBgColor ? 1 : 0));
});
const bgColorPane = pane.addInput(PARAMS, 'bgColor').on('change', () => {
    renderer.setClearColor(new THREE.Color(PARAMS.bgColor));
});
bgColorPane.hidden = true;
pane.expanded = false;


// Tweaks Vapor
pane.addInput(PARAMS, 'vapor').on('change', () => {
    vaporFolder.hidden = !PARAMS.vapor;
});
const vaporFolder = pane.addFolder({ title: 'Vapor Tweaks' });
vaporFolder.hidden = !PARAMS.vapor;
const vBtn = vaporFolder.addButton({
    title: 'randomize!',
    label: 'vapor'
});
vBtn.on('click', () => {
    PARAMS.showInitial = Math.random() < 0.5;
    PARAMS.vaporNoiseType = randomInt(0, 6);
    fxMaterial.uniforms.uNType.value = PARAMS.vaporNoiseType;
    PARAMS.vaporNoiseIntensity = Math.random();
    fxMaterial.uniforms.uNoiseIntensity.value = remap(PARAMS.vaporNoiseIntensity, 0, 1, 0.5, 1);
    PARAMS.vaporNoiseVignette = Math.random() < 0.5;
    fxMaterial.uniforms.uVignette.value = PARAMS.vaporNoiseVignette ? 1 : 0;
    PARAMS.vaporNoiseAmount = Math.random();
    fxMaterial.uniforms.uNoise.value = remap(PARAMS.vaporNoiseAmount, 0, 1, 0.1, (PARAMS.vaporNoiseType >= 2 ? 100 : 25));
    PARAMS.vaporNoiseScale = Math.random();
    fxMaterial.uniforms.uNoiseScale.value = remap(PARAMS.vaporNoiseScale, 0, 1, 0.002, (PARAMS.vaporNoiseType >= 2 ? 0.5 : 0.1));
    PARAMS.vaporNoiseSpeed = Math.random() / 25;
    fxMaterial.uniforms.uNoiseSpeed.value = remap(PARAMS.vaporNoiseSpeed, 0, 1, 0, 20);
    pane.refresh();
});
vaporFolder.addInput(PARAMS, 'showInitial', { label: 'initial' });
vaporFolder.addInput(PARAMS, 'vaporNoiseType', {
    label: 'type',
    options: {
        simplex: 0,
        voronoi: 1,
        vornü_1: 2,
        vornü_2: 3,
        vornü_3: 4,
    },
}).on('change', () => {
    fxMaterial.uniforms.uNType.value = PARAMS.vaporNoiseType;
    fxMaterial.uniforms.uNoise.value = remap(PARAMS.vaporNoiseAmount, 0, 1, 0.1, (PARAMS.vaporNoiseType >= 2 ? 100 : 25));
    fxMaterial.uniforms.uNoiseScale.value = remap(PARAMS.vaporNoiseScale, 0, 1, 0.002, (PARAMS.vaporNoiseType >= 2 ? 0.5 : 0.1));
});
vaporFolder.addInput(PARAMS, 'vaporNoiseIntensity', { label: 'intensity', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uNoiseIntensity.value = remap(PARAMS.vaporNoiseIntensity, 0, 1, 0.5, 1);
});
vaporFolder.addInput(PARAMS, 'vaporNoiseVignette', { label: 'vignette' }).on('change', () => {
    fxMaterial.uniforms.uVignette.value = PARAMS.vaporNoiseVignette ? 1 : 0;
});
vaporFolder.addInput(PARAMS, 'vaporNoiseAmount', { label: 'amount', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uNoise.value = remap(PARAMS.vaporNoiseAmount, 0, 1, 0.1, (PARAMS.vaporNoiseType >= 2 ? 100 : 25));
});
vaporFolder.addInput(PARAMS, 'vaporNoiseScale', { label: 'scale', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uNoiseScale.value = remap(PARAMS.vaporNoiseScale, 0, 1, 0.002, (PARAMS.vaporNoiseType >= 2 ? 0.5 : 0.1));
});
vaporFolder.addInput(PARAMS, 'vaporNoiseSpeed', { label: 'speed', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uNoiseSpeed.value = remap(PARAMS.vaporNoiseSpeed, 0, 1, 0, 20);
});
vaporFolder.addInput(PARAMS, 'mouseReactive', { label: 'mouse' }).on('change', () => {
    mouseBlade.hidden = !PARAMS.mouseReactive;
    pane.refresh();
});
const mouseBlade = vaporFolder.addInput(PARAMS, 'mouseSpeed', { step: 0.01, min: 0, max: 1 });
mouseBlade.hidden = !PARAMS.mouseReactive;

// Tweaks Noise
pane.addInput(PARAMS, 'grungifyNoise', { label: 'noise' }).on('change', () => {
    grunfigyNoiseFolder.hidden = !PARAMS.grungifyNoise;
    textureMaterial.uniforms.uNoise.value = PARAMS.grungifyNoise ? 1.0 : 0.0;
    patternMaterial.uniforms.uNoise.value = PARAMS.grungifyNoise ? 1.0 : 0.0;
    custom3DUniforms.uNoise.value = PARAMS.grungifyNoise ? 1.0 : 0.0;;
    pane.refresh();
});
const grunfigyNoiseFolder = pane.addFolder({ title: 'Noise Tweaks' });
grunfigyNoiseFolder.hidden = !PARAMS.grungifyNoise;
grunfigyNoiseFolder.addInput(PARAMS, 'grungifyNoiseIntensity', { label: 'intensity', step: 0.01, min: 0, max: 1 }).on('change', () => {
    textureMaterial.uniforms.uNoiseIntensity.value = PARAMS.grungifyNoiseIntensity;
    patternMaterial.uniforms.uNoiseIntensity.value = PARAMS.grungifyNoiseIntensity;
    custom3DUniforms.uNoiseIntensity.value = PARAMS.grungifyNoiseIntensity;
});
grunfigyNoiseFolder.addInput(PARAMS, 'grungifyNoiseScale', { label: 'scale', step: 0.01, min: 0, max: 1 }).on('change', () => {
    textureMaterial.uniforms.uNoiseScale.value = remap(PARAMS.grungifyNoiseScale, 0, 1, 0.01, 20);
    patternMaterial.uniforms.uNoiseScale.value = remap(PARAMS.grungifyNoiseScale, 0, 1, 0.01, 20);
    custom3DUniforms.uNoiseScale.value = remap(PARAMS.grungifyNoiseScale, 0, 1, 0.01, 20);
});
grunfigyNoiseFolder.addInput(PARAMS, 'grungifyNoiseSpeed', { label: 'speed', step: 0.01, min: 0, max: 1 }).on('change', () => {
    textureMaterial.uniforms.uNoiseSpeed.value = remap(PARAMS.grungifyNoiseSpeed, 0, 1, 0, 10);
    patternMaterial.uniforms.uNoiseSpeed.value = remap(PARAMS.grungifyNoiseSpeed, 0, 1, 0, 10);
    custom3DUniforms.uNoiseSpeed.value = remap(PARAMS.grungifyNoiseSpeed, 0, 1, 0, 10);
});
grunfigyNoiseFolder.addInput(PARAMS, 'grungifyLines', { label: 'lines', step: 1, min: 0, max: 4096 }).on('change', () => {
    textureMaterial.uniforms.uLines.value = PARAMS.grungifyLines;
    patternMaterial.uniforms.uLines.value = PARAMS.grungifyLines;
    custom3DUniforms.uLines.value = PARAMS.grungifyLines;
});
grunfigyNoiseFolder.addInput(PARAMS, 'grungifyLineIntensity', { label: 'linetensity', step: 0.01, min: 0, max: 1 }).on('change', () => {
    textureMaterial.uniforms.uLineIntensity.value = PARAMS.grungifyLineIntensity;
    patternMaterial.uniforms.uLineIntensity.value = PARAMS.grungifyLineIntensity;
    custom3DUniforms.uLineIntensity.value = PARAMS.grungifyLineIntensity;
});

// Tweaks Glitch
pane.addInput(PARAMS, 'grungifyGlitch', { label: 'glitch' }).on('change', () => {
    grunfigyGlitchFolder.hidden = !PARAMS.grungifyGlitch;
    fxMaterial.uniforms.uGlitch.value = PARAMS.grungifyGlitch ? 1.0 : 0.0;
    pane.refresh();
});
const grunfigyGlitchFolder = pane.addFolder({ title: 'Glitch Tweaks' });
grunfigyGlitchFolder.hidden = !PARAMS.grungifyGlitch;
const dbtn = grunfigyGlitchFolder.addButton({
    title: 'randomize!',
    label: 'glitch'
});
dbtn.on('click', () => {
    PARAMS.gltichType = randomInt(0, 3);
    fxMaterial.uniforms.uGType.value = PARAMS.gltichType;
    PARAMS.glitchSep = Math.random();
    fxMaterial.uniforms.uGAmount.value = PARAMS.glitchSep / 50;
    PARAMS.glitchAngle = Math.random();
    fxMaterial.uniforms.uGAngle.value = remap(PARAMS.glitchAngle, 0, 1, -Math.PI, Math.PI);
    PARAMS.glitchSeed = Math.random();
    fxMaterial.uniforms.uGSeed.value = PARAMS.glitchSeed;
    PARAMS.glitchSeedX = remap(Math.random(), 0, 1, -1, 1);
    fxMaterial.uniforms.uGSeedX.value = PARAMS.glitchSeedX;
    PARAMS.glitchSeedY = remap(Math.random(), 0, 1, -1, 1);
    fxMaterial.uniforms.uGSeedY.value = PARAMS.glitchSeedY;
    PARAMS.glitchDistortionX = Math.random();
    fxMaterial.uniforms.uGlitchionX.value = PARAMS.glitchDistortionX;
    PARAMS.glitchDistortionY = Math.random();
    fxMaterial.uniforms.uGlitchionY.value = PARAMS.glitchDistortionY;
    fxMaterial.uniforms.uGColumns.value = PARAMS.glitchColumns;
    PARAMS.glitchColumns = Math.random();
    pane.refresh();
});
grunfigyGlitchFolder.addInput(PARAMS, 'gltichType', {
    label: 'type',
    options: {
        first: 0,
        second: 1,
        third: 2,
    },
}).on('change', () => {
    fxMaterial.uniforms.uGType.value = PARAMS.gltichType;
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchSep', { label: 'sep', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGAmount.value = PARAMS.glitchSep / 50;
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchAngle', { label: 'angle', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGAngle.value = remap(PARAMS.glitchAngle, 0, 1, -Math.PI, Math.PI);
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchSeed', { label: 'seed root', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGSeed.value = PARAMS.glitchSeed;
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchSeedX', { label: 'seed x', step: 0.01, min: -1, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGSeedX.value = PARAMS.glitchSeedX;
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchSeedY', { label: 'seed y', step: 0.01, min: -1, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGSeedY.value = PARAMS.glitchSeedY;
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchDistortionX', { label: 'x distort', step: 0.01, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGlitchionX.value = PARAMS.glitchDistortionX;
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchDistortionY', { label: 'y distort', step: 0.01, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGlitchionY.value = PARAMS.glitchDistortionY;
});
grunfigyGlitchFolder.addInput(PARAMS, 'glitchColumns', { label: 'columns', step: 0.001, min: 0, max: 1 }).on('change', () => {
    fxMaterial.uniforms.uGColumns.value = PARAMS.glitchColumns;
});


// Tweaks 2D
const twoFolder = pane.addFolder({ title: '2D Tweaks' });
twoFolder.hidden = PARAMS.mode != 0;
twoFolder.addInput(PARAMS, 'image', {
    options: {
        script: 0,
        mark: 1,
        hippo: 2
    },
}).on('change', () => {
    loadImage();
});
twoFolder.addInput(PARAMS, 'twodeeColor1', { label: 'color 1', view: 'color' }).on('change', () => {
    textureMaterial.uniforms.uColor1.value = new THREE.Color(PARAMS.twodeeColor1);
});
twoFolder.addInput(PARAMS, 'twodeeColor2', { label: 'color 2', view: 'color' }).on('change', () => {
    textureMaterial.uniforms.uColor2.value = new THREE.Color(PARAMS.twodeeColor2);
});
twoFolder.addInput(PARAMS, 'colorSaturation', { step: 0.01, min: 0, max: 1 }).on('change', () => {
    textureMaterial.uniforms.uSat.value = remap(PARAMS.colorSaturation, 0, 1, 0, 5);
    patternMaterial.uniforms.uSat.value = remap(PARAMS.colorSaturation, 0, 1, 0, 5);
});
twoFolder.addInput(PARAMS, 'twodeerotate', { label: 'rotate' }).on('change', () => {
    gsap.to(textureMesh.rotation, { y: 0, ease: "power2.inout", duration: 0.75 });
    gsap.to(textureMesh.rotation, { x: 0, ease: "power2.inout", duration: 0.75 });
});
twoFolder.addInput(PARAMS, 'zoom', { step: 0.001, min: 0.1, max: 1 }).on('change', () => { renderImageTexture(); });

// Tweaks 3D
const threeFolder = pane.addFolder({ title: '3D Tweaks' });
threeFolder.hidden = PARAMS.mode == 0;
threeFolder.addInput(PARAMS, 'hemiLight').on('change', () => {
    hemiFolder.hidden = !PARAMS.hemiLight;
    if (PARAMS.hemiLight) { scene.add(fmarkHemiLight); }
    else { scene.remove(fmarkHemiLight); }
});
const hemiFolder = threeFolder.addFolder({ title: 'Hemisphere Light Settings' });
hemiFolder.hidden = !PARAMS.hemiLight;
hemiFolder.addInput(PARAMS, 'hemiColor1', { view: 'color' }).on('change', () => {
    fmarkHemiLight.color = new THREE.Color(PARAMS.hemiColor1);
});
hemiFolder.addInput(PARAMS, 'hemiColor2', { view: 'color' }).on('change', () => {
    fmarkHemiLight.groundColor = new THREE.Color(PARAMS.hemiColor2);
});
hemiFolder.addInput(PARAMS, 'hemiIntensity', { step: 0.001, min: 0, max: 2 }).on('change', () => {
    fmarkHemiLight.intensity = PARAMS.hemiIntensity;
});
threeFolder.addInput(PARAMS, 'dirLight').on('change', () => {
    dirFolder.hidden = !PARAMS.dirLight;
    if (PARAMS.dirLight) { scene.add(fmarkDirLight); }
    else { scene.remove(fmarkDirLight); }
});
const dirFolder = threeFolder.addFolder({ title: 'Directional Light Settings' });
dirFolder.hidden = !PARAMS.dirLight;
dirFolder.addInput(PARAMS, 'dirColor', { view: 'color' }).on('change', () => {
    fmarkDirLight.color = new THREE.Color(PARAMS.dirColor);
});
dirFolder.addInput(PARAMS, 'dirIntensity', { step: 0.001, min: 0, max: 2 }).on('change', () => {
    fmarkDirLight.intensity = PARAMS.dirIntensity;
});

// Tweaks Pattern
const gridFolder = pane.addFolder({ title: 'Pattern Tweaks' });
gridFolder.hidden = PARAMS.mode != 2;
const pBtn = gridFolder.addButton({
    title: 'randomize!',
    label: 'pattern'
});
pBtn.on('click', () => {
    PARAMS.shape = randomInt(0, shapeTextures.length);
    PARAMS.columns = randomInt(5, 101);
    PARAMS.rows = randomInt(5, 101);
    PARAMS.wide = remap(Math.random(), 0, 1, 0.1, 1);
    PARAMS.tall = remap(Math.random(), 0, 1, 0.1, 1);
    PARAMS.speed = remap(Math.random(), 0, 1, 0.01, 0.5);
    PARAMS.amp = remap(Math.random(), 0, 1, -1, 1);
    PARAMS.freq = remap(Math.random(), 0, 1, -1, 1);
    PARAMS.scale = Math.random();
    PARAMS.rainbow = Math.random() <= 0.4;
    const c = Math.floor(Math.random() * 16777215).toString(16);
    PARAMS.patternColor1 = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
    patternMaterial.uniforms.uColor1.value = new THREE.Color(PARAMS.patternColor1);
    PARAMS.patternColor2 = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
    patternMaterial.uniforms.uColor2.value = new THREE.Color(PARAMS.patternColor2);
    PARAMS.regular = Math.random() <= 0.5;
    PARAMS.sticky = Math.random() <= 0.5;
    PARAMS.explosion = (PARAMS.sticky ? 0 : Math.random() <= 0.15);
    PARAMS.bigWarp = Math.random() <= 0.15;
    pane.refresh();
    loadShapeTexture();
    buildPattern();
});
gridFolder.addInput(PARAMS, 'twodeerotate', { label: 'rotate' }).on('change', () => {
    gsap.to(patternMesh.rotation, { y: 0, ease: "power2.inout", duration: 0.75 });
    gsap.to(patternMesh.rotation, { x: 0, ease: "power2.inout", duration: 0.75 });
});
let folder = gridFolder.addFolder({ title: 'Shape' });
folder.addInput(PARAMS, 'shape', {
    options: {
        square: 0,
        dot: 1,
        rounded: 2,
        triangle: 3,
        squiggle: 4,
        bolt: 5,
        zigzag: 6
    },
}).on('change', () => {
    loadShapeTexture();
});
folder.addInput(PARAMS, 'wide', { label: 'shape width', min: 0, max: 1, step: 0.01 }).on('change', () => { buildPattern(); });
folder.addInput(PARAMS, 'tall', { label: 'shape height', min: 0, max: 1, step: 0.01 }).on('change', () => { buildPattern(); });
folder.addInput(PARAMS, 'speed', { min: 0.01, max: 1, step: 0.01 }).on('change', () => { patternMaterial.uniforms.uSpeed.value = PARAMS.speed; });
folder.addInput(PARAMS, 'freq', { min: -1, max: 1, step: 0.01 }).on('change', () => { patternMaterial.uniforms.uFreq.value = remap(PARAMS.freq, -1, 1, -0.01, 0.01) });
folder.addInput(PARAMS, 'amp', { min: -1, max: 1, step: 0.01 }).on('change', () => { patternMaterial.uniforms.uAmp.value = PARAMS.amp });
folder.addInput(PARAMS, 'scale', { min: 0, max: 1, step: 0.01 }).on('change', () => { patternMaterial.uniforms.uScale.value = PARAMS.scale; });
folder.addInput(PARAMS, 'rainbow').on('change', () => {
    patternMaterial.uniforms.uBow.value = PARAMS.rainbow ? 0.0 : 1.0;
    shapeColor1Blade.hidden = PARAMS.rainbow;
    shapeColor2Blade.hidden = PARAMS.rainbow;
    shapeSatBlade.hidden = PARAMS.rainbow;
    pane.refresh();
});
const shapeColor1Blade = folder.addInput(PARAMS, 'patternColor1', { label: 'color 1', view: 'color' }).on('change', () => { patternMaterial.uniforms.uColor1.value = new THREE.Color(PARAMS.patternColor1); });
const shapeColor2Blade = folder.addInput(PARAMS, 'patternColor2', { label: 'color 2', view: 'color' }).on('change', () => { patternMaterial.uniforms.uColor2.value = new THREE.Color(PARAMS.patternColor2); });
const shapeSatBlade = folder.addInput(PARAMS, 'colorSaturation', { step: 0.01, min: 0, max: 1 }).on('change', () => {
    textureMaterial.uniforms.uSat.value = remap(PARAMS.colorSaturation, 0, 1, 0, 5);
    patternMaterial.uniforms.uSat.value = remap(PARAMS.colorSaturation, 0, 1, 0, 5);
});
shapeColor1Blade.hidden = PARAMS.rainbow;
shapeColor2Blade.hidden = PARAMS.rainbow;
shapeSatBlade.hidden = PARAMS.rainbow;
const gridOptions = gridFolder.addFolder({ title: "Grid" });
gridOptions.addInput(PARAMS, 'columns', { min: 1, max: 250, step: 1 }).on('change', () => { buildPattern(); });
gridOptions.addInput(PARAMS, 'rows', { min: 1, max: 250, step: 1 }).on('change', () => { buildPattern(); });
gridOptions.addInput(PARAMS, 'regular').on('change', () => { patternMaterial.uniforms.uRegular.value = PARAMS.regular ? 0.0 : 1.0; });
gridOptions.addInput(PARAMS, 'sticky').on('change', () => { patternMaterial.uniforms.uSticky.value = PARAMS.sticky ? 0.0 : 1.0; });
gridOptions.addInput(PARAMS, 'explosion').on('change', () => { patternMaterial.uniforms.uExplosion.value = PARAMS.explosion ? 0.0 : 1.0; });
gridOptions.addInput(PARAMS, 'bigWarp').on('change', () => { patternMaterial.uniforms.uBigWarp.value = PARAMS.bigWarp ? 0.0 : 1.0; });

// Tweaks PP
const ppFolder = pane.addFolder({ title: 'Postprocessing Effects' });
ppFolder.addInput(PARAMS, 'sobel').on('change', () => {
    buildComposition();
})
ppFolder.addInput(PARAMS, 'bloom').on('change', () => {
    bStrengthBlade.hidden = !PARAMS.bloom;
    bRadiusBlade.hidden = !PARAMS.bloom;
    bThreshBlade.hidden = !PARAMS.bloom;
    pane.refresh();
    buildComposition();
})
const bStrengthBlade = ppFolder.addInput(PARAMS, 'bloomStrength', { label: 'strength', step: 0.001, min: 0, max: 2 }).on('change', () => { bloomEffect.strength = PARAMS.bloomStrength })
bStrengthBlade.hidden = true
const bRadiusBlade = ppFolder.addInput(PARAMS, 'bloomRadius', { label: 'radius', step: 0.001, min: 0, max: 2 }).on('change', () => { bloomEffect.radius = PARAMS.bloomRadius })
bRadiusBlade.hidden = true
const bThreshBlade = ppFolder.addInput(PARAMS, 'bloomThreshold', { label: 'threshold', step: 0.001, min: 0, max: 1 }).on('change', () => { bloomEffect.threshold = PARAMS.bloomThreshold })
bThreshBlade.hidden = true
ppFolder.addInput(PARAMS, 'rgbShift').on('change', () => {
    rgbStrengthBlade.hidden = !PARAMS.rgbShift;
    rgbAngleBlade.hidden = !PARAMS.rgbShift;
    pane.refresh();
    buildComposition();
})
const rgbStrengthBlade = ppFolder.addInput(PARAMS, 'rgbShiftAmount', { label: 'amount', step: 0.001, min: 0, max: 1 }).on('change', () => { rgbShiftEffect.uniforms.amount.value = PARAMS.rgbShiftAmount });
rgbStrengthBlade.hidden = true;
const rgbAngleBlade = ppFolder.addInput(PARAMS, 'rgbShiftAngle', { label: 'angle', step: 0.001, min: 0, max: Math.PI * 2 }).on('change', () => { rgbShiftEffect.uniforms.angle.value = PARAMS.rgbShiftAngle });
rgbAngleBlade.hidden = true;

// Basic Setup
const canvas = document.querySelector('canvas.webgl');
const clock = new THREE.Clock();
var lastElapsed = 0;
const scene = new THREE.Scene();

// Post processing 
var bloomEffect, rgbShiftEffect, sobelEffect, isPP;
var passes = [];

const buildComposition = () => {

    isPP = false;

    passes.forEach((pass) => {
        ppComposer.removePass(pass);
    });
    passes = [];
    ppComposer.reset(ppRenderTarget);

    passes.forEach((pass) => {
        ppComposer.removePass(pass);
    });
    passes = [];
    ppComposer.reset(ppRenderTarget);

    var pass = new ClearPass();
    ppComposer.addPass(pass);

    // TODO: Figure out how to get this to work when vapor is turned off 
    pass = new RenderPass(fxScene, camera);
    pass.clear = false;
    ppComposer.addPass(pass);

    pass = new RenderPass(scene, camera);
    pass.clear = false;
    ppComposer.addPass(pass);

    if (PARAMS.sobel) {
        isPP = true;
        if (!sobelEffect) {
            sobelEffect = new ShaderPass(SobelOperatorShader);
            sobelEffect.uniforms.resolution.value = new THREE.Vector2(SIZES.width, SIZES.height);
        }
        ppComposer.addPass(sobelEffect);
    }

    if (PARAMS.rgbShift) {
        isPP = true;
        if (!rgbShiftEffect) {
            rgbShiftEffect = new ShaderPass(RGBShiftShader);
            rgbShiftEffect.uniforms.amount.value = PARAMS.rgbShiftAmount;
            rgbShiftEffect.uniforms.angle.value = PARAMS.rgbShiftAngle;
        }
        ppComposer.addPass(rgbShiftEffect);
    }

    if (PARAMS.bloom) {
        isPP = true;
        if (!bloomEffect) {
            bloomEffect = new UnrealBloomPass();
            bloomEffect.strength = PARAMS.bloomStrength;
            bloomEffect.radius = PARAMS.bloomRadius;
            bloomEffect.threshold = PARAMS.bloomThreshold;
        }
        ppComposer.addPass(bloomEffect);
    }

    pass = new ShaderPass(CopyShader);
    ppComposer.addPass(pass);
};

// Shader Pattern
var cellSize = 1;

const pVertexShader = /*glsl*/`
    #define PI 3.14159265359    
    uniform float uTime;
    uniform float uSpeed;
    uniform float uAmp;
    uniform float uFreq;
    uniform float uScale;
    uniform float uRegular;
    uniform float uSticky;
    uniform float uExplosion;
    uniform float uBigWarp;

    attribute vec3 translate;

    varying vec2 vUv;
    varying float vNoise;

    //	Simplex 3D Noise 
    //	by Ian McEwan, Ashima Arts
    //
    vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

    float snoise(vec3 v){ 
    const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
    const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

    // First corner
    vec3 i  = floor(v + dot(v, C.yyy) );
    vec3 x0 =   v - i + dot(i, C.xxx) ;

    // Other corners
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min( g.xyz, l.zxy );
    vec3 i2 = max( g.xyz, l.zxy );

    //  x0 = x0 - 0. + 0.0 * C 
    vec3 x1 = x0 - i1 + 1.0 * C.xxx;
    vec3 x2 = x0 - i2 + 2.0 * C.xxx;
    vec3 x3 = x0 - 1. + 3.0 * C.xxx;

    // Permutations
    i = mod(i, 289.0 ); 
    vec4 p = permute( permute( permute( 
                i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
            + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

    // Gradients
    // ( N*N points uniformly over a square, mapped onto an octahedron.)
    float n_ = 1.0/7.0; // N=7
    vec3  ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4( x.xy, y.xy );
    vec4 b1 = vec4( x.zw, y.zw );

    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

    vec3 p0 = vec3(a0.xy,h.x);
    vec3 p1 = vec3(a0.zw,h.y);
    vec3 p2 = vec3(a1.xy,h.z);
    vec3 p3 = vec3(a1.zw,h.w);

    //Normalise gradients
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

    // Mix final noise value
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                    dot(p2,x2), dot(p3,x3) ) );
    }

    float noise(vec3 tr,float t) {
        vec3 trTime = vec3(tr.x + t,tr.y + t,tr.z + t);
        return sin( trTime.x * 2.1 ) + sin( trTime.y * 3.2 ) + sin( trTime.z * 4.3 );
    }

    vec2 rotate2D(vec2 st, float a) {
        st -= 0.5;
        st = mat2(cos(a),-sin(a),
                  sin(a),cos(a))*st;
        st += 0.5;
        return st;
    }

    float map(float value, float inMin, float inMax, float outMin, float outMax) {
        return outMin + (outMax - outMin) * (value - inMin) / (inMax - inMin);
    }

    void main()
    {
        vec4 mvPosition = modelViewMatrix * vec4( translate, 1.0 );
        float noise = snoise(vec3((translate.xy*uFreq),uTime*uSpeed));

        //rotate individuals
        vec2 pos = position.xy;
        mvPosition.xy += rotate2D(pos.xy,noise*uAmp*PI); 
        (uBigWarp == 0. ? mvPosition.xy += rotate2D(mvPosition.xy,noise*uAmp*noise*uAmp*20.) : mvPosition.xy += 0.); 

        float scale = map(noise*uScale, 0., 1., 0.1,10.);

        mvPosition.xyz += (uRegular == 0. ? position * scale : position);
        mvPosition.xyz *= (uSticky == 0. ? (step(scale,0.01) < 0.01 ? 0. : step(scale,0.01)) : 1.);
        mvPosition.xyz *= (uExplosion == 0. ? scale : 1.);

        gl_Position = projectionMatrix * mvPosition;
        
        vNoise = noise;
        vUv = uv;
    }
`;

const pFragmentShader = /*glsl*/`
    uniform sampler2D uMap;
    uniform vec3 uColor1;
    uniform vec3 uColor2;
    uniform float uBow;
    uniform float uTime;
    uniform float uNoise;
    uniform float uNoiseIntensity;
    uniform float uNoiseSpeed;
    uniform float uNoiseScale;
    uniform float uLineIntensity;
    uniform float uLines;
    uniform float uSat;

    varying vec2 vUv;
    varying float vNoise;

    //	Simplex 3D Noise 
    //	by Ian McEwan, Ashima Arts
    //
    vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

    float snoise(vec3 v){ 
        const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
        const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

        // First corner
        vec3 i  = floor(v + dot(v, C.yyy) );
        vec3 x0 =   v - i + dot(i, C.xxx) ;

        // Other corners
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min( g.xyz, l.zxy );
        vec3 i2 = max( g.xyz, l.zxy );

        //  x0 = x0 - 0. + 0.0 * C 
        vec3 x1 = x0 - i1 + 1.0 * C.xxx;
        vec3 x2 = x0 - i2 + 2.0 * C.xxx;
        vec3 x3 = x0 - 1. + 3.0 * C.xxx;

        // Permutations
        i = mod(i, 289.0 ); 
        vec4 p = permute( permute( permute( 
                    i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
                + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

        // Gradients
        // ( N*N points uniformly over a square, mapped onto an octahedron.)
        float n_ = 1.0/7.0; // N=7
        vec3  ns = n_ * D.wyz - D.xzx;

        vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

        vec4 x = x_ *ns.x + ns.yyyy;
        vec4 y = y_ *ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);

        vec4 b0 = vec4( x.xy, y.xy );
        vec4 b1 = vec4( x.zw, y.zw );

        vec4 s0 = floor(b0)*2.0 + 1.0;
        vec4 s1 = floor(b1)*2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));

        vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
        vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

        vec3 p0 = vec3(a0.xy,h.x);
        vec3 p1 = vec3(a0.zw,h.y);
        vec3 p2 = vec3(a1.xy,h.z);
        vec3 p3 = vec3(a1.zw,h.w);

        //Normalise gradients
        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;

        // Mix final noise value
        vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
        m = m * m;
        return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                        dot(p2,x2), dot(p3,x3) ) );
    }

    vec3 adjustSaturation(vec3 color, float value) {
        // https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
        const vec3 luminosityFactor = vec3(0.2126, 0.7152, 0.0722);
        vec3 grayscale = vec3(dot(color, luminosityFactor));

        return mix(grayscale, color, 1.0 + value);
    }

    // HSL to RGB Convertion helpers
    vec3 HUEtoRGB(float H){
        H = mod(H,1.0);
        float R = abs(H * 6.0 - 3.0) - 1.0;
        float G = 2.0 - abs(H * 6.0 - 2.0);
        float B = 2.0 - abs(H * 6.0 - 4.0);
        return clamp(vec3(R,G,B),0.0,1.0);
    }

    vec3 HSLtoRGB(vec3 HSL){
        vec3 RGB = HUEtoRGB(HSL.x);
        float C = (1.0 - abs(2.0 * HSL.z - 1.0)) * HSL.y;
        return (RGB - 0.5) * C + HSL.z;
    }

    void main() {
        vec4 diffuseColor = texture2D( uMap, vUv );
        if (diffuseColor.a < 0.9) {
            discard;
        } else {
            vec3 color = (uBow != 1. ? HSLtoRGB(vec3(vNoise/2.5, 1.0, 0.5)) : vec3(mix(uColor1,uColor2,1.-vUv.y)));
            if(uNoise == 1.){
                // make some noise
                vec2 uv = gl_FragCoord.xy;
                uv /= uNoiseScale;
                float n = snoise(vec3(uv, uTime * uNoiseSpeed));
                // add noise
                vec3 colorNoise = vec3(n * 0.5 + 0.5);
                color *= mix(colorNoise * uNoiseIntensity * 2., color, 1. - uNoiseIntensity);
                // get us a sine and cosine
                vec2 sc = vec2( sin( vUv.y * uLines ), cos( vUv.y * uLines ) );
                // add scanlines
                color += color * vec3( sc.x, sc.y, sc.x ) * uLineIntensity;
            }
            color = adjustSaturation(color, uSat);
            gl_FragColor =  vec4(diffuseColor.xyz * color, diffuseColor.w);
        }
    }
`;

const patternMaterial = new THREE.ShaderMaterial({
    vertexShader: pVertexShader,
    fragmentShader: pFragmentShader,
    transparent: true,
    uniforms: {
        'uMap': { value: null },
        'uTime': { value: 0.0 },
        'uSpeed': { value: 1.0 },
        'uColor1': { value: new THREE.Color(PARAMS.patternColor1) },
        'uColor2': { value: new THREE.Color(PARAMS.patternColor2) },
        'uBow': { value: PARAMS.rainbow ? 0.0 : 1.0 },
        'uAmp': { value: PARAMS.amp },
        'uFreq': { value: PARAMS.freq },
        'uScale': { value: PARAMS.scale },
        'uRegular': { value: PARAMS.regular ? 0.0 : 1.0 },
        'uSticky': { value: PARAMS.sticky ? 0.0 : 1.0 },
        'uExplosion': { value: PARAMS.explosion ? 0.0 : 1.0 },
        'uBigWarp': { value: PARAMS.bigWarp ? 0.0 : 1.0 },
        'uTime': { value: 0 },
        'uNoise': { value: PARAMS.grungifyNoise ? 1.0 : 0.0 },
        'uNoiseIntensity': { value: PARAMS.grungifyNoiseIntensity },
        'uNoiseScale': { value: remap(PARAMS.grungifyNoiseScale, 0, 1, 0.01, 20) },
        'uNoiseSpeed': { value: remap(PARAMS.grungifyNoiseSpeed, 0, 1, 0, 10) },
        'uLineIntensity': { value: PARAMS.grungifyLineIntensity },
        'uLines': { value: PARAMS.grungifyLines },
        'uSat': { value: remap(PARAMS.colorSaturation, 0, 1, 0, 5) }
    }
});

const textureLoader = new THREE.TextureLoader();
var patternMesh, patternTexture;

const shapeTextures = [new URL('./square.png', import.meta.url),
new URL('./circle.png', import.meta.url),
new URL('./rounded.png', import.meta.url),
new URL('./triangle.png', import.meta.url),
new URL('./squiggle.png', import.meta.url),
new URL('./bolt.png', import.meta.url),
new URL('./zigzag.png', import.meta.url)];
const loadShapeTexture = () => {
    textureLoader.load(shapeTextures[PARAMS.shape], function (tex) {
        patternMaterial.uniforms.uMap.value = tex;
        if (patternTexture) { patternTexture.dispose(); }
        patternTexture = tex;
    });
}

const buildPattern = () => {

    hidePattern();

    /**
     * Geometry and mesh
     */
    const dimension = Math.max(SIZES.width, SIZES.height);
    const w = dimension / PARAMS.columns;
    const padw = w * (1 - PARAMS.wide);
    const h = dimension / PARAMS.rows;
    const padh = h * (1 - PARAMS.tall);
    const gw = w - padw;
    const gh = h - padh;
    cellSize = Math.max(w, h);
    const geom = new THREE.PlaneGeometry(gw, gh);

    const geometry = new THREE.InstancedBufferGeometry();
    geometry.index = geom.index;
    geometry.attributes = geom.attributes;

    const particleCount = PARAMS.columns * PARAMS.rows;

    const translateArray = new Float32Array(particleCount * 3);

    for (let i = 0, i3 = 0; i < particleCount; i++, i3 += 3) {
        if (PARAMS.threedee) {
            translateArray[i3 + 0] = (Math.random() * 2 - 1) * dimension;
            translateArray[i3 + 1] = (Math.random() * 2 - 1) * dimension;
            translateArray[i3 + 2] = (Math.random() * 2 - 1) * dimension;
        }
        else {
            const col = i % PARAMS.columns;
            const row = Math.floor(i / PARAMS.columns);
            const x = (col * w + w) - (dimension * 0.5 + w * 0.5);
            const y = (row * h + h) - (dimension * 0.5 + h * 0.5);
            translateArray[i3 + 0] = x;
            translateArray[i3 + 1] = y;
            translateArray[i3 + 2] = 0;
        }

    }

    if (!patternMesh) {
        patternMesh = new THREE.Mesh(geometry, patternMaterial);
    }
    else {
        patternMesh.geometry = geometry;
    }
    patternMesh.rotation.x = 0;
    patternMesh.rotation.y = 0;
    patternMesh.geometry.setAttribute('translate', new THREE.InstancedBufferAttribute(translateArray, 3));
    patternMesh.geometry.needsUpdate = true;
    patternMaterial.needsUpdate = true;

    scene.add(patternMesh);
}

const loadPattern = () => {
    if (!patternMesh) {
        loadShapeTexture();
        buildPattern();
    }
    else { scene.add(patternMesh); }
};

const hidePattern = () => {
    if (!patternMesh) { return; }
    scene.remove(patternMesh);
}

// Shader 3D
const modelVertexShader = /*glsl*/`
varying vec2 v_uv;
    void main () {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        v_uv = uv;
    }
`;

const modelFragmentShader = /*glsl*/`
    uniform vec3 uColor1;
    uniform vec3 uColor2;
    uniform float uTime;
    varying vec2 v_uv;

    float wave(vec2 p, float angle) {
    vec2 direction = vec2(cos(angle), sin(angle));
    return cos(dot(p, direction));
    }

    float wrap(float x) {
    return abs(mod(x, 2.)-1.);
    }

    void main () {
        vec2 p = (v_uv - 0.5) * 50.;

        float brightness = 0.;
        for (float i = 1.; i <= 11.; i++) {
        brightness += wave(p, uTime / i);
        }

        brightness = wrap(brightness);

        gl_FragColor = vec4(mix(uColor1,uColor2,brightness),1.);
    }
`;

const gltfLoader = new GLTFLoader();
let fmarkMesh, fmarkHemiLight, fmarkDirLight, fmarkMaterial;
const loadModel = () => {
    if (!fmarkMesh) {
        PARAMS.loaded = false;
        gltfLoader.load(
            new URL('./fmark.glb', import.meta.url).href,
            (gltf) => {
                fmarkMesh = gltf.scene.children[0];
                fmarkMesh.geometry.scale(250, 250, 500)
                // fmarkMaterial = new THREE.ShaderMaterial({
                //     uniforms: {
                //         uTime: { value: 0 },
                //         uColor1: { value: new THREE.Vector3(1, 0, 0) },
                //         uColor2: { value: new THREE.Vector3(1, 1, 0) }
                //     },
                //     vertexShader: modelVertexShader,
                //     fragmentShader: modelFragmentShader
                // });
                fmarkMaterial = new THREE.MeshPhongMaterial({ color: '#8AC' });
                fmarkMaterial.onBeforeCompile = (shader) => {
                    shader.uniforms.uTime = custom3DUniforms.uTime;
                    shader.uniforms.uNoise = custom3DUniforms.uNoise;
                    shader.uniforms.uNoiseIntensity = custom3DUniforms.uNoiseIntensity;
                    shader.uniforms.uNoiseSpeed = custom3DUniforms.uNoiseSpeed;
                    shader.uniforms.uNoiseScale = custom3DUniforms.uNoiseScale;
                    shader.uniforms.uLineIntensity = custom3DUniforms.uLineIntensity;
                    shader.uniforms.uLines = custom3DUniforms.uLines;

                    shader.fragmentShader = shader.fragmentShader.replace(
                        'uniform float opacity;',
                        `
                            uniform float opacity;
                            uniform float uTime;
                            uniform float uNoise;
                            uniform float uNoiseIntensity;
                            uniform float uNoiseSpeed;
                            uniform float uNoiseScale;
                            uniform float uLineIntensity;
                            uniform float uLines;

                            //	Simplex 3D Noise 
                            //	by Ian McEwan, Ashima Arts
                            //
                            vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
                            vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

                            float snoise(vec3 v){ 
                                const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
                                const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

                                // First corner
                                vec3 i  = floor(v + dot(v, C.yyy) );
                                vec3 x0 =   v - i + dot(i, C.xxx) ;

                                // Other corners
                                vec3 g = step(x0.yzx, x0.xyz);
                                vec3 l = 1.0 - g;
                                vec3 i1 = min( g.xyz, l.zxy );
                                vec3 i2 = max( g.xyz, l.zxy );

                                //  x0 = x0 - 0. + 0.0 * C 
                                vec3 x1 = x0 - i1 + 1.0 * C.xxx;
                                vec3 x2 = x0 - i2 + 2.0 * C.xxx;
                                vec3 x3 = x0 - 1. + 3.0 * C.xxx;

                                // Permutations
                                i = mod(i, 289.0 ); 
                                vec4 p = permute( permute( permute( 
                                            i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                                        + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
                                        + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

                                // Gradients
                                // ( N*N points uniformly over a square, mapped onto an octahedron.)
                                float n_ = 1.0/7.0; // N=7
                                vec3  ns = n_ * D.wyz - D.xzx;

                                vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

                                vec4 x_ = floor(j * ns.z);
                                vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

                                vec4 x = x_ *ns.x + ns.yyyy;
                                vec4 y = y_ *ns.x + ns.yyyy;
                                vec4 h = 1.0 - abs(x) - abs(y);

                                vec4 b0 = vec4( x.xy, y.xy );
                                vec4 b1 = vec4( x.zw, y.zw );

                                vec4 s0 = floor(b0)*2.0 + 1.0;
                                vec4 s1 = floor(b1)*2.0 + 1.0;
                                vec4 sh = -step(h, vec4(0.0));

                                vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
                                vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

                                vec3 p0 = vec3(a0.xy,h.x);
                                vec3 p1 = vec3(a0.zw,h.y);
                                vec3 p2 = vec3(a1.xy,h.z);
                                vec3 p3 = vec3(a1.zw,h.w);

                                //Normalise gradients
                                vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
                                p0 *= norm.x;
                                p1 *= norm.y;
                                p2 *= norm.z;
                                p3 *= norm.w;

                                // Mix final noise value
                                vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
                                m = m * m;
                                return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                                                dot(p2,x2), dot(p3,x3) ) );
                            }
                        `
                    )

                    shader.fragmentShader = shader.fragmentShader.replace(
                        'vec3 outgoingLight = reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance;',
                        `
                            vec3 color = vec3(1.);
                            if(uNoise == 1.){
                                // make some noise
                                vec2 uv = gl_FragCoord.xy;
                                uv /= uNoiseScale;
                                float n = snoise(vec3(uv, uTime * uNoiseSpeed));
                                // add noise
                                vec3 colorNoise = vec3(n * 0.5 + 0.5);
                                color = mix(colorNoise * uNoiseIntensity * 2., color, 1. - uNoiseIntensity);
                                // get us a sine and cosine
                                vec2 sc = vec2( sin( uv.y * uLines ), cos( uv.y * uLines ) );
                                // add scanlines
                                color += color * vec3( sc.x, sc.y, sc.x ) * uLineIntensity;
                            }
                            vec3 outgoingLight = (reflectedLight.directDiffuse + reflectedLight.indirectDiffuse + reflectedLight.directSpecular + reflectedLight.indirectSpecular + totalEmissiveRadiance) * color;
                        `
                    )
                }

                fmarkMesh.material = fmarkMaterial;
                fmarkMesh.castShadow = true;
                fmarkMesh.receiveShadow = true;
                scene.add(fmarkMesh);
                fmarkHemiLight = new THREE.HemisphereLight(PARAMS.hemiColor1, PARAMS.hemiColor2, PARAMS.hemiIntensity);
                scene.add(fmarkHemiLight);
                fmarkDirLight = new THREE.DirectionalLight(PARAMS.dirColor, PARAMS.dirIntensity);
                fmarkDirLight.position.set(50, 200, 100);
                fmarkDirLight.target.position.set(0, 0, 0);
                fmarkDirLight.castShadow = true;
                fmarkDirLight.shadow.mapSize.width = 1024;
                fmarkDirLight.shadow.mapSize.height = 1024;
                fmarkDirLight.shadow.camera.near = 0.5;
                fmarkDirLight.shadow.camera.far = 500;
                fmarkDirLight.shadow.camera.left = -250;
                fmarkDirLight.shadow.camera.right = 250;
                fmarkDirLight.shadow.camera.top = 250;
                fmarkDirLight.shadow.camera.bottom = -250;
                scene.add(fmarkDirLight);
                scene.add(fmarkDirLight.target);
                PARAMS.loaded = true;
            }
        )
    }
    else {
        scene.add(fmarkMesh);
        scene.add(fmarkHemiLight);
        scene.add(fmarkDirLight);
    }
};
const hideModel = () => {
    if (!fmarkMesh) { return; }
    scene.remove(fmarkMesh);
    scene.remove(fmarkHemiLight);
    scene.remove(fmarkDirLight);
};

// Image Texture
const textureCanvas = document.createElement('canvas');
textureCanvas.width = SIZES.width;
textureCanvas.height = SIZES.height;
const ctx = textureCanvas.getContext('2d');
var textureImg = new Image();
textureImg.onload = function () {
    scene.add(textureMesh);
    renderImageTexture();
    PARAMS.loaded = true;
}
const svgUrls = [new URL('./fscript.png', import.meta.url),
new URL('./fmark.png', import.meta.url),
new URL('./hippo.png', import.meta.url)];
const loadImage = () => {
    PARAMS.loaded = false;
    textureImg.src = svgUrls[PARAMS.image];
};
const hideImage = () => {
    if (!textureMesh) { return; }
    scene.remove(textureMesh);
}

const updateExperiment = () => {
    if (PARAMS.mode == 0) {
        loadImage();
        hideModel();
        hidePattern();
    }
    else if (PARAMS.mode == 1) {
        hideImage();
        loadModel();
        hidePattern();
    }
    else {
        hideImage();
        hideModel();
        loadPattern();
    }
    buildComposition();
}

const overrideExperiment = (option = 0) => {
    // TODO: Add other otions
    // square: 0,
    // dot: 1,
    // rounded: 2,
    // triangle: 3,
    // squiggle: 4,
    // bolt: 5,
    // zigzag: 6

    PARAMS.mode = 2;

    switch (option) {
        case 0:
            PARAMS.vapor = true;
            PARAMS.mouseReactive = false;
            PARAMS.showInitial = false;
            PARAMS.vaporNoiseType = 4;
            fxMaterial.uniforms.uNType.value = PARAMS.vaporNoiseType;
            PARAMS.vaporNoiseIntensity = 0.75;
            fxMaterial.uniforms.uNoiseIntensity.value = remap(PARAMS.vaporNoiseIntensity, 0, 1, 0.5, 1);
            PARAMS.vaporNoiseVignette = false;
            fxMaterial.uniforms.uVignette.value = PARAMS.vaporNoiseVignette ? 1 : 0;
            PARAMS.vaporNoiseAmount = 0.9;
            fxMaterial.uniforms.uNoise.value = remap(PARAMS.vaporNoiseAmount, 0, 1, 0.1, (PARAMS.vaporNoiseType >= 2 ? 100 : 25));
            PARAMS.vaporNoiseScale = 0.006;
            fxMaterial.uniforms.uNoiseScale.value = remap(PARAMS.vaporNoiseScale, 0, 1, 0.002, (PARAMS.vaporNoiseType >= 2 ? 0.5 : 0.1));
            PARAMS.vaporNoiseSpeed = 0.001;
            fxMaterial.uniforms.uNoiseSpeed.value = remap(PARAMS.vaporNoiseSpeed, 0, 1, 0, 20);
            PARAMS.shape = 0;
            PARAMS.columns = 98;
            PARAMS.rows = 13;
            PARAMS.wide = 0.32;
            PARAMS.tall = 0.26;
            PARAMS.speed = 0.02;
            PARAMS.amp = 0.86;
            PARAMS.freq = -1;
            PARAMS.scale = 0.88;
            PARAMS.rainbow = false;
            PARAMS.patternColor1 = '#80868D';
            patternMaterial.uniforms.uColor1.value = new THREE.Color(PARAMS.patternColor1);
            PARAMS.patternColor2 = '#49897A';
            patternMaterial.uniforms.uColor2.value = new THREE.Color(PARAMS.patternColor2);
            PARAMS.regular = true;
            PARAMS.sticky = true;
            PARAMS.explosion = false;
            PARAMS.bigWarp = false;
            break;

        default:
            break;
    }

    pane.refresh();
    updateExperiment();
};

// Shader Image

const texVertexShader = /*glsl*/`
    varying vec2 vUv;

    void main () {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        vUv = uv;
    }
`;

const texFragmentShader = /*glsl*/`
    uniform sampler2D sampler;
    uniform float uTime;
    uniform float uNoise;
    uniform float uNoiseIntensity;
    uniform float uNoiseSpeed;
    uniform float uNoiseScale;
    uniform float uLineIntensity;
    uniform float uLines;
    uniform vec3 uColor1;
    uniform vec3 uColor2;
    uniform float uSat;
    varying vec2 vUv;

    //	Simplex 3D Noise 
    //	by Ian McEwan, Ashima Arts
    //
    vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

    float snoise(vec3 v){ 
        const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
        const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

        // First corner
        vec3 i  = floor(v + dot(v, C.yyy) );
        vec3 x0 =   v - i + dot(i, C.xxx) ;

        // Other corners
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min( g.xyz, l.zxy );
        vec3 i2 = max( g.xyz, l.zxy );

        //  x0 = x0 - 0. + 0.0 * C 
        vec3 x1 = x0 - i1 + 1.0 * C.xxx;
        vec3 x2 = x0 - i2 + 2.0 * C.xxx;
        vec3 x3 = x0 - 1. + 3.0 * C.xxx;

        // Permutations
        i = mod(i, 289.0 ); 
        vec4 p = permute( permute( permute( 
                    i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
                + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
                + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

        // Gradients
        // ( N*N points uniformly over a square, mapped onto an octahedron.)
        float n_ = 1.0/7.0; // N=7
        vec3  ns = n_ * D.wyz - D.xzx;

        vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

        vec4 x = x_ *ns.x + ns.yyyy;
        vec4 y = y_ *ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);

        vec4 b0 = vec4( x.xy, y.xy );
        vec4 b1 = vec4( x.zw, y.zw );

        vec4 s0 = floor(b0)*2.0 + 1.0;
        vec4 s1 = floor(b1)*2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));

        vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
        vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

        vec3 p0 = vec3(a0.xy,h.x);
        vec3 p1 = vec3(a0.zw,h.y);
        vec3 p2 = vec3(a1.xy,h.z);
        vec3 p3 = vec3(a1.zw,h.w);

        //Normalise gradients
        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;

        // Mix final noise value
        vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
        m = m * m;
        return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                        dot(p2,x2), dot(p3,x3) ) );
    }

    vec3 adjustSaturation(vec3 color, float value) {
        // https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
        const vec3 luminosityFactor = vec3(0.2126, 0.7152, 0.0722);
        vec3 grayscale = vec3(dot(color, luminosityFactor));

        return mix(grayscale, color, 1.0 + value);
    }

    void main () {
        vec4 texColor = texture2D(sampler, vUv);
        if (texColor.a < 0.9) {
            discard;
        } else {
            vec3 color = vec3(mix(uColor1,uColor2,1.-vUv.y));
            if(uNoise == 1.){
                // make some noise
                vec2 uv = gl_FragCoord.xy;
                uv /= uNoiseScale;
                float n = snoise(vec3(uv, uTime * uNoiseSpeed));
                // add noise
                vec3 colorNoise = vec3(n * 0.5 + 0.5);
                color *= mix(colorNoise * uNoiseIntensity * 2., color, 1. - uNoiseIntensity);
                // get us a sine and cosine
                vec2 sc = vec2( sin( vUv.y * uLines ), cos( vUv.y * uLines ) );
                // add scanlines
                color += color * vec3( sc.x, sc.y, sc.x ) * uLineIntensity;
            }
            color = adjustSaturation(color, uSat);
            gl_FragColor =  vec4(color, texColor.a);

        }
    }
`;

const textureGeometry = new THREE.PlaneGeometry(SIZES.width, SIZES.height);
const textureMaterial = new THREE.ShaderMaterial({
    uniforms: {
        sampler: { value: null },
        uTime: { value: 0 },
        uNoise: { value: PARAMS.grungifyNoise ? 1.0 : 0.0 },
        uNoiseIntensity: { value: PARAMS.grungifyNoiseIntensity },
        uNoiseScale: { value: remap(PARAMS.grungifyNoiseScale, 0, 1, 0.01, 20) },
        uNoiseSpeed: { value: remap(PARAMS.grungifyNoiseSpeed, 0, 1, 0, 10) },
        uLineIntensity: { value: PARAMS.grungifyLineIntensity },
        uLines: { value: PARAMS.grungifyLines },
        uColor1: { value: new THREE.Color(PARAMS.twodeeColor1) },
        uColor2: { value: new THREE.Color(PARAMS.twodeeColor2) },
        uSat: { value: remap(PARAMS.colorSaturation, 0, 1, 0, 5) }
    },
    vertexShader: texVertexShader,
    fragmentShader: texFragmentShader,
    transparent: true,
});
const textureMesh = new THREE.Mesh(textureGeometry, textureMaterial);

// Shader Vapor
const generateHeightmap = () => {
    const dt_size = 64;
    const data_arr = new Float32Array(dt_size * dt_size);
    const length = dt_size * dt_size;
    for (let i = 0; i < length; i++) {
        const val = randomFloat(0, 1);
        data_arr[i] = val;
    }
    const texture = new THREE.DataTexture(data_arr, dt_size, dt_size, THREE.RedFormat, THREE.FloatType);
    texture.needsUpdate = true;
    return texture;
}

const fxVertexShader = /*glsl*/`
    varying vec2 vUv;

    void main () {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        vUv = uv;
    }
`;

const fxFragmentShader = /*glsl*/`
    uniform sampler2D sampler;
    uniform int uGlitch;
    uniform int uNType;
    uniform int uGType;
    uniform sampler2D uGDisplace;
    uniform float uGAmount;
    uniform float uGAngle;
    uniform float uGSeed;
    uniform float uGSeedX;
    uniform float uGSeedY;
    uniform float uGlitchionX;
    uniform float uGlitchionY;
    uniform float uGColumns;
    uniform float uTime;
    uniform vec2 uMouse;
    uniform float uNoiseIntensity;
    uniform int uVignette;
    uniform float uNoise;
    uniform float uNoiseSpeed;
    uniform float uNoiseScale;
    varying vec2 vUv;

    #define M_PI 3.14159265358979323846

    float rand(vec2 n) { 
        return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
    }

    const mat2 myt = mat2(.12121212, .13131313, -.13131313, .12121212);
    const vec2 mys = vec2(1e4, 1e6);

    vec2 rhash(vec2 uv) {
    uv *= myt;
    uv *= mys;
    return fract(fract(uv / mys) * uv);
    }

    vec3 hash(vec3 p) {
    return fract(
        sin(vec3(dot(p, vec3(1.0, 57.0, 113.0)), dot(p, vec3(57.0, 113.0, 1.0)),
                dot(p, vec3(113.0, 1.0, 57.0)))) *
        43758.5453);
    }

    vec3 voronoi3d(const in vec3 x, const in int m) {
    vec3 p = floor(x);
    vec3 f = fract(x);

    float id = 0.0;
    vec2 res = vec2(100.0);
    for (int k = -1; k <= 1; k++) {
        for (int j = -1; j <= 1; j++) {
        for (int i = -1; i <= 1; i++) {
            vec3 b = vec3(float(i), float(j), float(k));
            vec3 r = vec3(b) - f + hash(p + b);
            float d = dot(r, r);

            float cond = max(sign(res.x - d), 0.0);
            float nCond = 1.0 - cond;

            float cond2 = nCond * max(sign(res.y - d), 0.0);
            float nCond2 = 1.0 - cond2;

            id = (dot(p + b, vec3(1.0, 57.0, 113.0)) * cond) + (id * nCond);
            res = vec2(d, res.x) * cond + res * nCond;

            if(m == 1){
                res.y = cond2 * d + nCond2 * res.y;
            }
            else if(m == 2){
                res.y = -cond2 * d + nCond2 * res.y;
            } 
            else if(m == 3){
                res.y = cond2 * d + nCond*-nCond2*-nCond2 * res.y;
            }
            else {
                res.y = cond * d - -nCond2 * res.x;
            }
        }
        }
    }

    return vec3(sqrt(res), abs(id));
    }

    //	Simplex 3D Noise 
    //	by Ian McEwan, Ashima Arts
    //
    vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x, 289.0);}
    vec4 taylorInvSqrt(vec4 r){return 1.79284291400159 - 0.85373472095314 * r;}

    float snoise(vec3 v){ 
    const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
    const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

    // First corner
    vec3 i  = floor(v + dot(v, C.yyy) );
    vec3 x0 =   v - i + dot(i, C.xxx) ;

    // Other corners
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min( g.xyz, l.zxy );
    vec3 i2 = max( g.xyz, l.zxy );

    //  x0 = x0 - 0. + 0.0 * C 
    vec3 x1 = x0 - i1 + 1.0 * C.xxx;
    vec3 x2 = x0 - i2 + 2.0 * C.xxx;
    vec3 x3 = x0 - 1. + 3.0 * C.xxx;

    // Permutations
    i = mod(i, 289.0 ); 
    vec4 p = permute( permute( permute( 
                i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
            + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

    // Gradients
    // ( N*N points uniformly over a square, mapped onto an octahedron.)
    float n_ = 1.0/7.0; // N=7
    vec3  ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4( x.xy, y.xy );
    vec4 b1 = vec4( x.zw, y.zw );

    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

    vec3 p0 = vec3(a0.xy,h.x);
    vec3 p1 = vec3(a0.zw,h.y);
    vec3 p2 = vec3(a1.xy,h.z);
    vec3 p3 = vec3(a1.zw,h.w);

    //Normalise gradients
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

    // Mix final noise value
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                    dot(p2,x2), dot(p3,x3) ) );
    }

    void main () {
        vec4 t0 = vec4(0);
        float v = (uVignette == 0 ? 0. : distance(vUv,vec2(0.5)));
        if(uNType<1){
            float a = snoise(vec3(vUv * uNoise, uTime * uNoiseSpeed)) * uNoiseScale;
            float b = snoise(vec3(vUv * uNoise, uTime * uNoiseSpeed + 100.)) * uNoiseScale;
            t0 = texture2D(sampler, vUv + vec2(a, b) + uMouse * 0.005);
        }
        else {
            vec3 a = voronoi3d(vec3(vUv * -uNoise, uTime * uNoiseSpeed), uNType) * uNoiseScale;
            vec3 b = -voronoi3d(vec3(vUv * uNoise, -uTime * uNoiseSpeed), uNType) * uNoiseScale;
            t0 = texture2D(sampler, vUv + a.xy + b.xy + uMouse * 0.005);
        }

        if(uGlitch>0) {
            vec2 p = vUv;
            float xs = floor(gl_FragCoord.x / 0.1);
            float ys = floor(gl_FragCoord.y / 0.1);
            //based on staffantans glitch shader for unity https://github.com/staffantan/unityglitch
            float disp = texture2D(uGDisplace, p*uGSeed*uGSeed).r;
            if(p.y<uGlitchionX+uGColumns && p.y>uGlitchionY-uGColumns*uGSeed) {
                if(uGSeedX>0.){
                    p.y = 1. - (p.y + uGlitchionY);
                }
                else {
                    p.y = uGlitchionY;
                }
            }
            if(p.x<uGlitchionY+uGColumns && p.x>uGlitchionY-uGColumns*uGSeed) {
                if(uGSeedY>0.){
                    p.x=uGlitchionX;
                }
                else {
                    p.x = 1. - (p.x + uGlitchionX);
                }
            }
            p.x+=disp*uGSeedX*(uGSeed/0.5);
            p.y+=disp*uGSeedY*(uGSeed/0.5);
            
            //base from RGB shift shader
            vec2 offset = uGAmount * vec2( cos(uGAngle), sin(uGAngle));
            vec4 cr = texture2D(sampler, p + offset);
            vec4 cga = texture2D(sampler, p);
            vec4 cb = texture2D(sampler, p - offset);
            vec4 finalColor = vec4(cr.r, cga.g, cb.b, cga.a);
            
            //add noise
            // vec4 snow = 200.*uGAmount*vec4(rand(vec2(xs * uGSeed,ys * uGSeed*50.))*0.2);
            // finalColor += snow;

            //this is starting to get somewhere
            if(uGType==0){
                gl_FragColor = vec4(t0.xyz - finalColor.xyz, uNoiseIntensity-v);
            }
            else if(uGType==1) {
                gl_FragColor = vec4(finalColor.xyz - t0.xyz, uNoiseIntensity-v);
            }
            else {
                gl_FragColor = vec4((t0.xyz - finalColor.xyz) / length(t0.xyz), uNoiseIntensity-v);
            }
            //this produces kinda alpha
            // gl_FragColor = vec4(t0.xyz * uColorPersist, uAlphaPersist * t0.a);
        }
        else {
            // gl_FragColor = vec4(t0.xyz, pow(uNoiseIntensity * 0.999, uTime));
            gl_FragColor = vec4(t0.xyz, uNoiseIntensity-v);
        }
    }
`;

const fxScene = new THREE.Scene();
const fxGeometry = new THREE.PlaneGeometry(SIZES.width, SIZES.height);
const fxMaterial = new THREE.ShaderMaterial({
    vertexShader: fxVertexShader,
    fragmentShader: fxFragmentShader,
    transparent: true,
    uniforms: {
        sampler: { value: null },
        uGlitch: { value: PARAMS.grungifyGlitch ? 1.0 : 0.0 },
        uNType: { value: PARAMS.vaporNoiseType },
        uGType: { value: PARAMS.gltichType },
        uGDisplace: { value: generateHeightmap() },
        uGAmount: { value: PARAMS.glitchSep / 50 },
        uGAngle: { value: remap(PARAMS.glitchAngle, 0, 1, -Math.PI, Math.PI) },
        uGSeed: { value: PARAMS.glitchSeed },
        uGSeedX: { value: PARAMS.glitchSeedX },
        uGSeedY: { value: PARAMS.glitchSeedY },
        uGlitchionX: { value: PARAMS.glitchDistortionX },
        uGlitchionY: { value: PARAMS.glitchDistortionY },
        uGColumns: { value: PARAMS.glitchColumns },
        uTime: { value: 0 },
        uMouse: { value: new THREE.Vector2(-1, -1) },
        uNoiseIntensity: { value: remap(PARAMS.vaporNoiseIntensity, 0, 1, 0.5, 1) },
        uVignette: { value: PARAMS.vaporNoiseVignette ? 1 : 0 },
        uNoise: { value: remap(PARAMS.vaporNoiseAmount, 0, 1, 0.1, (PARAMS.vaporNoiseType >= 2 ? 100 : 25)) },
        uNoiseSpeed: { value: remap(PARAMS.vaporNoiseSpeed, 0, 1, 0, 20) },
        uNoiseScale: { value: remap(PARAMS.vaporNoiseScale, 0, 1, 0.002, (PARAMS.vaporNoiseType >= 2 ? 0.5 : 0.1)) }
    }
});
const fxMesh = new THREE.Mesh(fxGeometry, fxMaterial);
fxScene.add(fxMesh);

const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    alpha: true,
    premultipliedAlpha: false
});
renderer.outputEncoding = THREE.sRGBEncoding;
renderer.setSize(SIZES.width, SIZES.height);
renderer.setPixelRatio(PIXEL_RATIO);
renderer.shadowMap.enabled = true;
renderer.setClearColor(new THREE.Color(PARAMS.bgColor));
renderer.setClearAlpha(0);
const camera = new THREE.OrthographicCamera(-SIZES.width * 0.5, SIZES.width * 0.5, SIZES.height * 0.5, -SIZES.height * 0.5, 0.1, 1000);
camera.position.set(0, 0, 500);
camera.aspect = SIZES.width / SIZES.height;

let renderBufferA = new THREE.WebGLRenderTarget(SIZES.width * PIXEL_RATIO, SIZES.height * PIXEL_RATIO, { format: THREE.RGBAFormat });
let renderBufferB = new THREE.WebGLRenderTarget(SIZES.width * PIXEL_RATIO, SIZES.height * PIXEL_RATIO, { format: THREE.RGBAFormat });

var ppPrameters = {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
    stencilBuffer: true
};
const ppRenderTarget = new THREE.WebGLRenderTarget(SIZES.width, SIZES.height, ppPrameters);
const ppComposer = new EffectComposer(renderer, ppRenderTarget);

const animationLoop = () => {
    if (!PARAMS.loaded) { return; }

    const elapsedTime = clock.getElapsedTime();

    if (PARAMS.vapor) {
        if (PARAMS.mouseReactive) {
            const mappedSpeed = remap(PARAMS.mouseSpeed, 0, 1, 0, 50);
            const mouseSpeed = (elapsedTime - lastElapsed) * mappedSpeed;
            lastElapsed = elapsedTime;
            MOUSE_POS.currentX += (MOUSE_POS.targetX - MOUSE_POS.currentX) * mouseSpeed;
            MOUSE_POS.currentY += (MOUSE_POS.targetY - MOUSE_POS.currentY) * mouseSpeed;

            fxMaterial.uniforms.uMouse.value.x = MOUSE_POS.currentX;
            fxMaterial.uniforms.uMouse.value.y = MOUSE_POS.currentY;
            // fxMaterial.uniforms.uGlitch.value = Math.random()<0.95;
        }

        fxMaterial.uniforms.sampler.value = renderBufferB.texture;
        fxMaterial.uniforms.uTime.value = elapsedTime;

        renderer.autoClearColor = false;

        renderer.setRenderTarget(renderBufferA);
        renderer.clearColor();
        renderer.render(fxScene, camera);
        renderer.render(scene, camera);
        renderer.setRenderTarget(null);
        renderer.render(fxScene, camera);
        if (PARAMS.showInitial) { renderer.render(scene, camera); }
        if (isPP) {
            ppComposer.render();
        }

        const temp = renderBufferA;
        renderBufferA = renderBufferB;
        renderBufferB = temp;
    }
    else {
        renderer.autoClearColor = true;
        renderer.render(scene, camera);
    }

    // if(fmarkMaterial) { fmarkMaterial.uniforms.uTime.value = elapsedTime; }
    custom3DUniforms.uTime.value = elapsedTime;
    if (fmarkMaterial) { fmarkMaterial.needsUpdate = true; }
    if (patternMaterial) { patternMaterial.uniforms.uTime.value = elapsedTime; }
    if (textureMaterial) { textureMaterial.uniforms.uTime.value = elapsedTime; }
};

window.addEventListener('load', () => {
    initMainTls();
    initJBI();
    initWKYII();
    document.querySelector('#btn-folder').addEventListener('click', handleBtnClick);
    document.querySelector('#btn-house').addEventListener('click', handleBtnClick);
    document.querySelector('#btn-star').addEventListener('click', handleBtnClick);
    var collection = document.querySelector('#section-notifications');
    var items = [...collection.children].slice(1);
    initCollectionItems(collection, items, 'section-notifications');
    collection = document.querySelector('#section-messages');
    items = [...collection.children].slice(1);
    initCollectionItems(collection, items, 'section-messages');
    collection = document.querySelector('#section-inbox');
    items = [...collection.children].slice(1);
    initCollectionItems(collection, items, 'section-inbox');

    // TODO: Incorporate threejs when background is part of it
    // // Start animation loop
    // renderer.setAnimationLoop(animationLoop);
    // // Light it up
    // updateExperiment();
    // window.setTimeout(() => { overrideExperiment(); }, 150);
});

var mainTls = [];
var wYWA, jBI, wKYII;
const initMainTls = () => {
    const dur = 0.65;
    var item = document.querySelector('#title-home');
    var offset = item.getBoundingClientRect().width;
    var tl = gsap.timeline({ paused: true });
    tl.fromTo(item, { opacity: 1 }, { opacity: 0, duration: dur * 0.9, delay: 0.3 }, 0);
    tl.fromTo(item, { x: 0 }, { x: -offset * 0.75, duration: dur, ease: "back.inOut(0.8)", delay: 0.2 }, 0);
    mainTls.push(tl);

    wYWA = document.querySelector('#wYWA');
    var offset = wYWA.getBoundingClientRect().width;
    var tl = gsap.timeline({ paused: true });
    tl.fromTo(wYWA, { opacity: 1 }, { opacity: 0, duration: dur * 0.9, delay: 0.2 }, 0);
    tl.fromTo(wYWA, { x: 0 }, { x: -offset * 0.75, duration: dur, ease: "back.inOut(0.8)", delay: 0.1 }, 0);
    mainTls.push(tl);

    jBI = document.querySelector('#jBI');
    var offset = jBI.getBoundingClientRect().width;
    var tl = gsap.timeline({ paused: true });
    tl.fromTo(jBI, { opacity: 1 }, { opacity: 0, duration: dur * 0.9, delay: 0 }, 0);
    tl.fromTo(jBI, { x: 0 }, { x: -offset * 0.75, duration: dur, ease: "back.inOut(0.8)", delay: 0 }, 0);
    mainTls.push(tl);

    item = document.querySelector('#title-reco');
    var offset = item.getBoundingClientRect().width;
    var tl = gsap.timeline({ paused: true });
    tl.fromTo(item, { opacity: 0 }, { opacity: 1, duration: dur * 0.9, delay: dur }, 0);
    tl.fromTo(item, { x: offset * 0.75 }, { x: 0, duration: dur, ease: "back.inOut(0.8)", delay: 0.3 }, 0);
    mainTls.push(tl);

    wKYII = document.querySelector('#wKYII');
    var offset = wKYII.getBoundingClientRect().x;
    var tl = gsap.timeline({ paused: true });
    tl.fromTo(wKYII, { opacity: 0 }, { opacity: 1, duration: dur * 0.9, delay: dur }, 0);
    tl.fromTo(wKYII, { x: offset * 0.75 }, { x: -offset, duration: dur, ease: "back.inOut(0.8)", delay: 0.3 }, 0);
    mainTls.push(tl);

    jBI.addEventListener('pointerenter', () => {
        wYWA.style.pointerEvents = 'none';
    });
    jBI.addEventListener('pointerleave', () => {
        wYWA.style.pointerEvents = 'all';
    });
}

const CARD_ROT = 6;
const Z_RANGE = { min: -150, max: -400 }
var curWKYII;
const initWKYII = () => {

    // algorithm for random-but-decent-looking-distribution
    // place first item within a certain tolerance of the center of the parent space
    // for each subsequent item, place item within a certain tolerance plus additional offset of the center 
    // if item overlaps another item by a tolerance failure then shift it in the opposite direction until tolerance is met
    // don't exceed item's dimensions

    const wKYIIDimen = wKYII.getBoundingClientRect();
    const t = { tol: 0.5, incr: 0.5, tolEr: 0.2 };
    const cards = gsap.utils.toArray('.wKYII-card');
    cards.forEach((ec, i) => {
        gsap.set(ec, { xPercent: -50, yPercent: -50 });
        const s = ec.getBoundingClientRect();
        const rx = Math.random();
        const ry = Math.random();
        var dx = (wKYIIDimen.width - s.width) * 0.5 * t.tol
        dx = dx + dx * (i * t.incr);
        var dy = (wKYIIDimen.height - s.height) * 0.5 * t.tol
        dy = dy + dy * (i * t.incr);
        const x = remap(rx, 0, 1, -dx, dx);
        const y = remap(ry, 0, 1, -dy, dy);
        ec.style.left = `${x + wKYIIDimen.width * 0.5}px`;
        ec.style.top = `${y + wKYIIDimen.height * 0.5}px`;
        const rand = Math.random();
        const tz = remap(rand, 0, 1, Z_RANGE.min, Z_RANGE.max);
        if (i < cards.length - 1) {
            gsap.set(ec, { translateZ: tz });
            ec.style.zIndex = 0;
        }
        else {
            curWKYII = ec;
            curWKYII.style.zIndex = 1;
        }
        ec.dataset.tz = tz;
        ec.addEventListener('pointermove', (e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const mouseX = -remap(e.clientX - r.left, 0, r.width, -CARD_ROT, CARD_ROT);
            const mouseY = remap(e.clientY - r.top, 0, r.height, -CARD_ROT, CARD_ROT);
            gsap.to(e.currentTarget, {
                duration: 0.5,
                rotationY: mouseX,
                rotationX: mouseY,
                ease: 'power2.out'
            })
        });
        ec.addEventListener('pointerleave', (e) => {
            gsap.to(e.currentTarget, {
                duration: 0.65,
                rotationY: 0,
                rotationX: 0,
                ease: 'elastic.out(0.6,0.3)'
            })
        });
        ec.addEventListener('pointerenter', (e) => {
            if (curWKYII === e.currentTarget) { return; }
            gsap.to(curWKYII, {
                duration: 1.65,
                translateZ: curWKYII.dataset.tz,
                ease: 'elastic.out(0.4,0.3)'
            })
            curWKYII.style.zIndex = 0;
            curWKYII = e.currentTarget;
            curWKYII.style.zIndex = 1;
            gsap.to(curWKYII, {
                duration: 0.45,
                translateZ: 0,
                ease: 'elastic.out(0.5,0.6)'
            })
        });
    });
}

var collections = [];
const initCollectionItems = (c, d, id) => {
    var tls = [];
    const offset = d[0].getBoundingClientRect().height;
    for (var i = 0; i < d.length; i++) {
        const tl = gsap.timeline({ paused: true });
        const op = d.length == 2 ? 0.4 : 1.1 - remap(i, 0, d.length - 1, 0.8, 0.4);
        const de = (d.length - 1 - i) * 0.05;
        tl.fromTo(d[i], { opacity: (i < d.length - 1 ? op : 1) }, { opacity: 1, delay: de, duration: 0.25 }, 0);
        tl.fromTo(d[i], { y: i * -offset * 0.8 }, { y: 0, ease: "elastic.out(1,0.8)", delay: de, duration: 0.65 }, 0);
        tl.fromTo(d[i], { scale: 1 }, { scale: 1.05, ease: "elastic.out(0.5,0.3)", delay: de, duration: 0.65 }, 0);
        tl.seek(0);
        tls.push(tl);
    }
    collections.push({ id: id, tls: tls });
    c.addEventListener('pointerenter', (e) => {
        collections.filter(c => c.id === e.target.id)[0].tls.forEach(tl => { tl.timeScale(1).play(); });
    });
    c.addEventListener('pointerleave', (e) => {
        collections.filter(c => c.id === e.target.id)[0].tls.forEach(tl => { tl.timeScale(1.2).reverse(); });
    });
}

const jbiSizes = { p: null, w: null };
const initJBI = () => {
    jbiSizes.p = document.querySelector('#jbi-personal').getBoundingClientRect();
    jbiSizes.w = document.querySelector('#jbi-work').getBoundingClientRect();
    initJBICore(document.querySelector('#jbi-personal'));
    initJBICore(document.querySelector('#jbi-work'));
}

var curSizes, curTargets;
const initJBICore = (d) => {
    gsap.set('.jbi-img', { xPercent: -50, yPercent: -50 });
    const t = 0.8;
    var info = d.id == 'jbi-personal' ? jbiSizes.p : jbiSizes.w;
    var offset = { w: info.width - (info.width * t), h: info.height - (info.height * t) };
    var items = [...d.children].slice(0, d.children.length - 1);
    items.forEach(item => {
        const s = item.getBoundingClientRect();
        const x = remap(Math.random(), 0, 1, offset.w, info.width - offset.w * 2);
        const y = remap(Math.random(), 0, 1, offset.h, info.height - offset.h * 2);
        item.style.left = `${x}px`;
        item.style.top = `${y + s.height * 0.15}px`;
    });
    d.addEventListener('pointerenter', (e) => {
        curSizes = e.target.id == 'jbi-personal' ? jbiSizes.p : jbiSizes.w;
        curTargets = gsap.utils.toArray(`${e.target.id == 'jbi-personal' ? '#jbi-personal' : '#jbi-work'} .jbi-img`);
        curTargets.forEach(el => { gsap.to(el, { borderColor: '#ffffff55', duration: 0.25 }); });
        window.addEventListener("mousemove", followMouse);
    });
    d.addEventListener('pointerleave', (e) => {
        curTargets = gsap.utils.toArray(`${e.target.id == 'jbi-personal' ? '#jbi-personal' : '#jbi-work'} .jbi-img`);
        curTargets.forEach(el => { gsap.to(el, { borderColor: '#ffffff0f', duration: 0.25 }); });
        window.removeEventListener("mousemove", followMouse);
        gsap.to(curTargets, {
            duration: 5,
            x: 0,
            y: 0,
            ease: "elastic.out(1,0.8)",
            overwrite: "auto",
            stagger: 0.05
        });
    });
}

//TODO: see new algorithm notes above
const followMouse = (e) => {
    gsap.to(curTargets, {
        duration: 10,
        x: e.clientX - curSizes.left - curSizes.width * 0.5,
        y: e.clientY - curSizes.top - curSizes.height * 0.5,
        ease: "elastic.out(1,0.8)",
        overwrite: "auto",
        stagger: 0.05
    });
}

var currentBtn = document.querySelector('#btn-house');
const handleBtnClick = (e) => {
    if (e.target.id == 'btn-folder' || e.target == currentBtn) { return; }
    currentBtn.classList.remove('selected');
    currentBtn = e.target;
    currentBtn.classList.add('selected');
    if (e.target.id == 'btn-star') {
        mainTls.forEach(tl => { tl.timeScale(1).play(); });
        gsap.to(document.querySelector('.bg1'), { opacity: 0, duration: 0.3, delay: 0.2 });
        gsap.to(document.querySelector('.bg2'), { opacity: 1, duration: 0.5, delay: 0 });
    }
    else {
        mainTls.forEach(tl => { tl.timeScale(0.8).reverse(); });
        gsap.to(document.querySelector('.bg1'), { opacity: 1, duration: 0.5, delay: 0 });
        gsap.to(document.querySelector('.bg2'), { opacity: 0, duration: 0.3, delay: 0.2 });
    }
    document.body.style.pointerEvents = 'none';
    window.setTimeout(() => { document.body.style.pointerEvents = 'all'; }, 750);
}

window.addEventListener('pointermove', (e) => {
    const x = (e.pageX / SIZES.width) * 2 - 1;
    const y = (1 - e.pageY / SIZES.height) * 2 - 1;
    MOUSE_POS.targetX = x;
    MOUSE_POS.targetY = y;
    if (fmarkMesh) {
        gsap.to(fmarkMesh.rotation, { y: -Math.PI * 0.25 * x, ease: "power2.inout", duration: 0.75 });
        gsap.to(fmarkMesh.rotation, { x: Math.PI * 0.25 * y, ease: "power2.inout", duration: 0.75 });
    }
    if (PARAMS.twodeerotate && textureMesh) {
        if (textureMesh) {
            gsap.to(textureMesh.rotation, { y: -Math.PI * 0.1 * x, ease: "power2.inout", duration: 0.75 });
            gsap.to(textureMesh.rotation, { x: Math.PI * 0.1 * y, ease: "power2.inout", duration: 0.75 });
        }
        if (patternMesh) {
            gsap.to(patternMesh.rotation, { y: -Math.PI * 0.1 * x, ease: "power2.inout", duration: 0.75 });
            gsap.to(patternMesh.rotation, { x: Math.PI * 0.1 * y, ease: "power2.inout", duration: 0.75 });
        }
    }
});

window.addEventListener('resize', () => {
    SIZES.width = window.innerWidth;
    SIZES.height = window.innerHeight;
    camera.aspect = SIZES.width / SIZES.height;
    camera.updateProjectionMatrix();
    renderer.setSize(SIZES.width, SIZES.height);
    renderBufferA.setSize(SIZES.width, SIZES.height);
    renderBufferB.setSize(SIZES.width, SIZES.height);
    if (PARAMS.mode == 0) { renderImageTexture(); }
});

var fxHidden = false;
var pHidden = true;
document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.key === "e") {
        canvas.style.display = fxHidden ? 'block' : 'none';
        fxHidden = !fxHidden;
    }
    if (e.ctrlKey && e.key === "p") {
        tpContainer.style.display = pHidden ? 'block' : 'none';
        pHidden = !pHidden;
    }
});

const renderImageTexture = () => {
    var w, h, a;
    if (textureImg.height > textureImg.width) {
        a = textureImg.width / textureImg.height;
        switch (PARAMS.image) {
            case 0:
                h = SIZES.height * PARAMS.zoom;
                break;
            case 1:
                h = SIZES.height * PARAMS.zoom * 0.5;
                break;
            case 2:
                h = SIZES.height * PARAMS.zoom * 0.35;
                break;
        }
        w = h * a;
    }
    else {
        a = textureImg.height / textureImg.width;
        w = SIZES.width * (PARAMS.image == 1 ? PARAMS.zoom * 0.5 : PARAMS.zoom);
        h = w * a;
    }
    ctx.clearRect(0, 0, SIZES.width, SIZES.height);
    ctx.drawImage(textureImg, (SIZES.width - w) * 0.5, (SIZES.height - h) * 0.5, w, h);
    textureMaterial.uniforms.sampler.value = new THREE.CanvasTexture(textureCanvas);
};